import React, { useContext, useState } from 'react';
import {
  useHistory,
  useLocation,
} from 'react-router-dom/cjs/react-router-dom.min';
import styled, { useTheme } from 'styled-components';
import Icon from 'components/Icon';
import Spacer from 'components/Spacer';
import ToolTip from 'components/Tooltip';
import { usePrintManager } from 'hooks/usePrintManager';
import { usePrintReceipt } from 'hooks/usePrintReceipt';
import { usePrintTickets } from 'hooks/usePrintTickets';
import { useStoreGetTransaction } from 'hooks/useStoreGetTransaction';
import { AppContext } from 'shared/AppContext';
import { useCart } from 'shared/CartContext';
import { getPrintSettings } from 'shared/utils/printUtils';
import MobileDeliveryModal from './MobileDeliveryModal';
import { PrintingModal } from './PrintingModal';

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  flex-grow: 1;
  min-width: 250px;
  min-height: 60vh;
`;

const ReprintIcon = styled.div`
  position: absolute;
  top: 12px;
  right: 50px;
  cursor: pointer;
  z-index: 1;
  /* Zoom on scroll */
  transform: scale(1);
  transition: transform 0.3s ease;
  &:hover {
    transform: scale(1.1);
  }
`;

const SettingsIcon = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  cursor: pointer;
  z-index: 1;

  /* Zoom on scroll */
  transform: scale(1);
  transition: transform 0.3s ease;
  &:hover {
    transform: scale(1.1);
  }
`;

const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 5px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.02)'};
  border-radius: 8px;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const EmptyText = styled.span`
  font-family: Barlow Condensed;
  font-size: 32px;
  font-weight: 700;
  line-height: 40px;
  text-align: center;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : '#000033'};
`;

const AddText = styled.span`
  font-family: Roboto;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  text-align: center;
  max-width: 60%;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.secondary || '#94A3B8'
      : '#64748B'};
`;

export const EmptyCart = ({ event }) => {
  const history = useHistory();
  const theme = useTheme();
  const iconColor = theme?.name === 'dark' ? '#ffffff' : '#64748B';

  const pathname = useLocation().pathname;
  const userType =
    ['admin-employee', 'admin-track'].find((type) => pathname.includes(type)) ||
    'admin';

  const [openSuccessModal, setOpenSuccessModal] = useState(false);

  const {
    account: eventAccount,
    setIsPrinting,
    isPrinting,
    setConnectionStatus,
    setCurrentStatus,
  } = useCart();

  const {
    state: { account },
  } = useContext(AppContext);

  const availableAccount = account || eventAccount;

  const { printSettingEnabled, printReceiptEnabled, printReceiptWithTickets } =
    getPrintSettings({ event, availableAccount });

  const { reprintLastReceipt } = usePrintReceipt();
  const { reprintLastTransaction } = usePrintTickets();
  const { getLastTransaction } = useStoreGetTransaction();

  const { connect, isConnected } = usePrintManager(); // Use the new hook

  const IPAddress = localStorage.getItem('readerIp');

  const printConfigOn =
    printSettingEnabled || printReceiptEnabled || printReceiptWithTickets;

  const printTicketAndReceipt = async () => {
    setCurrentStatus('Printing...');
    if (printSettingEnabled) {
      await reprintLastTransaction(setIsPrinting);

      // Print receipt with tickets only if the setting for printing tickets is enabled
      if (printReceiptWithTickets) await reprintLastReceipt(setIsPrinting);
    }

    if (printReceiptEnabled) {
      await reprintLastReceipt(setIsPrinting);
    }

    setIsPrinting(false);
    setOpenSuccessModal(true);
  };

  const reprintPurchase = async () => {
    setIsPrinting(true);
    const connectedPrinter = isConnected();
    if (!connectedPrinter && IPAddress) {
      setCurrentStatus('Connecting to printer...');
      try {
        const connected = await connect(IPAddress, setConnectionStatus, () =>
          console.error('Failed to connect to printer in EmptyCart')
        );

        if (connected) {
          printTicketAndReceipt();
        } else {
          console.error('Failed to connect to printer in EmptyCart');
        }
      } catch (error) {
        console.error('Failed to connect to printer reprint:', error);
      }
    } else if (connectedPrinter) {
      printTicketAndReceipt();
    }
  };

  const openSettings = () => {
    history.push(`/${userType}/events/posSettings/${event.id}`, {
      event: event,
    });
  };

  return (
    <Wrapper>
      {getLastTransaction() && printConfigOn && (
        <ToolTip content={'Reprint Last Purchase'} preservePosition={true}>
          <ReprintIcon onClick={reprintPurchase}>
            <Icon icon="Print-Icon" size={24} color={iconColor} />
          </ReprintIcon>
        </ToolTip>
      )}
      <SettingsIcon onClick={openSettings}>
        <Icon icon="settings-icon" size={24} color={iconColor} />
      </SettingsIcon>
      <Container>
        <Icon icon="shopping_cart" size={128} color={iconColor} />
        <Spacer size={15} />
        <EmptyText>Empty Cart</EmptyText>
        <AddText>Add items by selecting options in the left columns.</AddText>
      </Container>

      <PrintingModal
        isVisible={isPrinting}
        printSettingEnabled={printSettingEnabled}
        printReceiptEnabled={printReceiptEnabled}
        printReceiptWithTickets={printReceiptWithTickets}
      />

      <MobileDeliveryModal
        isVisible={openSuccessModal}
        setIsVisible={setOpenSuccessModal}
      />
    </Wrapper>
  );
};
